// Pick Three: the weekly game. Name the three FBS teams you expect to score the
// most lineup points this week. No league, and the teams need not be rostered.
//
// The score is the sum of the three teams' weekly points for that week, shown
// against the best possible three. Nothing is scored on the server: the points
// are the weekly_points the rest of the app already reads from the teams map.
// The server only stores the picks and enforces the locks (migration 084).
//
// Each team locks on its own, 15 minutes before its kickoff, the same rule as
// lineups and free agency. A locked team can be neither added nor removed. The
// week stays open until the last team locks, so a player can still enter on
// Saturday with whatever has not kicked off.
//
// One of the three is the captain, and its points count double, wins and
// losses alike (migration 085). There is no 5x Play or freeze: those belong to
// the season game.
import { LOCK_LEAD_MS, formatSpreadValue, getTeamSpread, startOfEasternDay } from "../utils/gameStatus";
import { isScoringWeek, TOTAL_LINEUP_WEEKS } from "../utils/season";
import { normalizeTeamName } from "../utils/teamName";
import { teamLogoUrl } from "../utils/teamLogo";
import { rankTopTeams } from "../utils/topTeams";

export const GAME_ID = "pick-three";
export const GAME_NAME = "Pick Three";
export const PICKS = 3;

/** The lineup week as a number, or null in the off-season and before week 1. */
export function pickWeek(currentWeek) {
  const week = parseInt(String(currentWeek ?? "").replace(/\D/g, ""), 10);
  if (!Number.isInteger(week) || !isScoringWeek(week) || week < 1 || week > TOTAL_LINEUP_WEEKS) return null;
  return week;
}

const isFbs = (teams, slug) => teams?.[normalizeTeamName(slug || "")]?.classification === "fbs";
const involvesFbs = (row, teams) => isFbs(teams, row.home_team) || isFbs(teams, row.away_team);

/**
 * When a team stops being pickable: 15 minutes before its kickoff, or the start
 * of its gameday in Eastern when the kickoff time is not confirmed (the stored
 * time is then a midnight-Eastern placeholder, later than the real kickoff).
 * Null with no kickoff: a bye never locks. Mirrors pick_three_team_lock_at in
 * migration 084, which is what holds.
 */
export function lockAtFor(kickoff, tbd) {
  if (!kickoff) return null;
  const date = new Date(kickoff);
  if (Number.isNaN(date.getTime())) return null;
  return tbd ? startOfEasternDay(date) : new Date(date.getTime() - LOCK_LEAD_MS);
}

/** Whether a picker row's team has locked. */
export const isRowLocked = (row, now = new Date()) => Boolean(row?.lockAt) && now >= row.lockAt;

/**
 * The next lock still to come and the last of the week, from a list of lock
 * times. Shared by both windows below.
 */
function lockSpan(locks, now) {
  const times = locks.filter(Boolean);
  if (!times.length) return { nextLockAt: null, lastLockAt: null };
  const future = times.filter((t) => t > now);
  return {
    nextLockAt: future.length ? new Date(Math.min(...future)) : null,
    lastLockAt: new Date(Math.max(...times)),
  };
}

/**
 * Where the week stands, from its raw `games` rows.
 *
 *   unavailable  no FBS game with a date: the schedule is not imported yet
 *   open         at least one team has still to lock, so picks can change
 *   live         every team has locked, and a game is still to finish
 *   final        every FBS game is complete
 *
 * `kickoff` is the first game's start, `{ date, tbd }`, for the "starts" line.
 * `nextLockAt` is the next team to lock, `lastLockAt` when picks close for good.
 */
export function weekWindow(rows, teams, now = new Date()) {
  const games = (rows || []).filter((row) => row.date && involvesFbs(row, teams));
  if (!games.length) return { phase: "unavailable", kickoff: null, nextLockAt: null, lastLockAt: null };

  const locks = games.map((row) => lockAtFor(row.date, row.start_time_tbd));
  let first = 0;
  locks.forEach((lock, i) => { if (lock < locks[first]) first = i; });
  const kickoff = { date: new Date(games[first].date), tbd: Boolean(games[first].start_time_tbd) };
  const span = lockSpan(locks, now);

  let phase = now >= span.lastLockAt ? "live" : "open";
  if (games.every((row) => row.game_complete)) phase = "final";
  return { phase, kickoff, ...span };
}

const TIME_PARTS = {
  timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
};

/** "Thu Oct 8, 7:30pm ET". */
export function formatEastern(date) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", TIME_PARTS).formatToParts(date).map((p) => [p.type, p.value]));
  return `${parts.weekday} ${parts.month} ${parts.day}, ${parts.hour}:${parts.minute}${parts.dayPeriod.toLowerCase()} ET`;
}

/** "7:30pm": a kickoff time in Eastern. */
export function formatEasternTime(date) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }).formatToParts(date).map((p) => [p.type, p.value]),
  );
  return `${parts.hour}:${parts.minute}${parts.dayPeriod.toLowerCase()}`;
}

/** "Thursday": for a game whose kickoff time is still to be confirmed. */
export function formatEasternDay(date) {
  return new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", weekday: "long" }).format(date);
}

/**
 * The lines the card and the pages show about the window: when the week starts
 * and when picks close for good. Null when there is no window to describe.
 *
 * There is deliberately no "next team locks" countdown: each row shows its own
 * lock, so the screens state the rule (LOCK_RULE) instead.
 */
export function describeWindow(window) {
  if (!window?.kickoff || !window.lastLockAt) return null;
  const { kickoff, lastLockAt } = window;
  return {
    starts: kickoff.tbd ? `${formatEasternDay(kickoff.date)}, time to be confirmed` : formatEastern(kickoff.date),
    last: formatEastern(lastLockAt),
  };
}

/** How locking works, in one line, under the week's dates on every screen. */
export const LOCK_RULE = "Each team locks 15 minutes before its own kickoff. A locked pick cannot be changed.";

/**
 * Every FBS team as a picker row for one week. `gamesBySlug` is
 * buildGamesBySlug of that week's rows. A team with no game is on a bye: it
 * can be picked, and scores zero, which the row says. `lockAt` is when the
 * team stops being pickable (null for a bye).
 */
/**
 * A team's form going into `week`: its points last week (null for a bye, or in
 * week 1 when there is no last week) and its average over the weeks it has
 * scored so far, to one place (null before it has scored). Week 0 awards no
 * points and the current week is still being played, so both stop at last
 * week. pick_three_slate (migration 087) works out the same two numbers for a
 * signed-out visitor.
 */
export function teamForm(weeklyPoints, week) {
  const last = week - 1;
  const played = Object.entries(weeklyPoints || {})
    .filter(([w, p]) => Number(w) >= 1 && Number(w) <= last && typeof p === "number")
    .map(([, p]) => p);
  const lastWeek = last >= 1 ? weeklyPoints?.[String(last)] : undefined;
  return {
    lastWeek: typeof lastWeek === "number" ? lastWeek : null,
    seasonAvg: played.length ? Math.round((played.reduce((a, b) => a + b, 0) / played.length) * 10) / 10 : null,
  };
}

/** Picker rows for `week`. `week` only feeds the form line; without it there is none. */
export function buildPickRows(teams, gamesBySlug, week = null) {
  return Object.keys(teams || {})
    .filter((slug) => teams[slug]?.classification === "fbs")
    .map((slug) => {
      const team = teams[slug];
      const game = gamesBySlug?.[slug] || null;
      const opponent = game ? teams[game.awayTeam] : null;
      return {
        slug,
        name: team.name,
        logo: team.logo,
        conference: team.conference && team.conference !== "Unknown" ? team.conference : null,
        bye: !game,
        isHome: game?.isHome ?? null,
        opponent: game ? (opponent?.name || game.awayTeam) : null,
        kickoff: game?.date ? new Date(game.date) : null,
        tbd: Boolean(game?.startTimeTbd),
        lockAt: game ? lockAtFor(game.date, game.startTimeTbd) : null,
        spread: game ? getTeamSpread(game, slug) : null,
        formWeek: week,
        ...(week ? teamForm(team.currentSeason?.weeklyPoints, week) : { lastWeek: null, seasonAvg: null }),
      };
    });
}

/** The conferences among `rows`, A to Z, for the picker's filter. */
export function conferencesOf(rows) {
  return [...new Set((rows || []).map((r) => r.conference).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

export const SORTS = [
  { id: "kickoff", label: "Kickoff" },
  { id: "lastWeek", label: "Last week" },
  { id: "favourite", label: "Biggest favourite" },
  { id: "name", label: "A to Z" },
];

/** `rows` in the order `sort` names. Byes and teams with no line go last. */
export function sortPickRows(rows, sort) {
  const byName = (a, b) => a.name.localeCompare(b.name);
  const copy = [...rows];
  if (sort === "name") return copy.sort(byName);
  // Highest scorers last week first. A bye has no score, so it goes last.
  if (sort === "lastWeek") {
    return copy.sort((a, b) => (b.lastWeek ?? -Infinity) - (a.lastWeek ?? -Infinity) || byName(a, b));
  }
  if (sort === "favourite") {
    return copy.sort((a, b) => (a.spread ?? Infinity) - (b.spread ?? Infinity) || byName(a, b));
  }
  return copy.sort((a, b) => {
    if (a.bye !== b.bye) return a.bye ? 1 : -1;
    return (a.kickoff?.getTime() ?? Infinity) - (b.kickoff?.getTime() ?? Infinity) || byName(a, b);
  });
}

/** "vs Purdue", "at Purdue", or "Bye week". */
export function matchupLabel(row) {
  if (row.bye) return "Bye week. Scores 0.";
  return `${row.isHome ? "vs" : "at"} ${row.opponent}`;
}

/**
 * "-27.5" or "+3". "No line" for a team that plays but has no spread yet, which
 * is most of them early in the week; nothing for a bye, where the row already
 * says so.
 */
export const spreadLabel = (row) => (row.bye ? "" : formatSpreadValue(row.spread) || "No line");

/**
 * The player's score for a week.
 *
 * `picks` are slugs and `captain` one of them (or null, for an entry saved
 * before captains existed). Each row carries the team's `points` and what it
 * adds to the total, `scored`: double for the captain. A pick with no entry for
 * the week (a bye, or a game not yet scored) counts zero.
 *
 * `best` is the three highest-scoring FBS teams of the week. The best possible
 * total captains the top one, so it is their sum plus the top score again. A
 * pick counts as one of the best three when it scored at least as much as the
 * third, so a tie for third never leaves a tied pick unmarked.
 */
export function scoreEntry(teams, picks, week, captain = null) {
  const pointsOf = (slug) => {
    const value = teams?.[slug]?.currentSeason?.weeklyPoints?.[String(week)];
    return typeof value === "number" ? value : null;
  };
  // Every team at or above the third-best score, so ties at the cut are known.
  const ranked = rankTopTeams(teams, { mode: "week", week, limit: Infinity });
  const best = ranked.slice(0, PICKS);
  const cutoff = best.length === PICKS ? best[PICKS - 1].points : null;
  // Teams outside the top three that scored the same as the third: any of
  // them would have counted just as well.
  const alsoBest = cutoff === null ? [] : ranked.slice(PICKS).filter((r) => r.points === cutoff);

  const rows = (picks || []).map((slug) => {
    const points = pointsOf(slug);
    const isCaptain = slug === captain;
    return {
      slug,
      name: teams?.[slug]?.name || slug,
      logo: teams?.[slug]?.logo,
      points,
      isCaptain,
      scored: points === null ? null : points * (isCaptain ? 2 : 1),
      inBest: points !== null && cutoff !== null && points >= cutoff,
    };
  });

  return {
    rows,
    total: rows.reduce((sum, r) => sum + (r.scored ?? 0), 0),
    best,
    alsoBest,
    cutoff,
    bestTotal: best.reduce((sum, r) => sum + r.points, 0) + (best[0]?.points ?? 0),
  };
}

/** The captain rule, in one line, wherever picks are made. */
export const CAPTAIN_RULE = "Tap a pick to make it your captain. Its points count double, wins and losses alike.";

/**
 * Whether the captain can move to `target`. Not onto a team that has locked
 * (that would double a result already seen) and not off a captain that has
 * locked (that would take the double away from a team that is losing). Mirrors
 * save_pick_three in migration 085, which is what holds.
 */
export function canMakeCaptain(current, target, isLocked) {
  if (!target || target === current) return false;
  if (isLocked(target)) return false;
  return !(current && isLocked(current));
}

/** Where a shared score lands. The signed-out page opens on the picker. */
export const SHARE_URL = "https://www.lineupcfb.com/pick-three";

/**
 * The share sheet text: one square per pick, green when it was in the best
 * three, the score, where it finished when that is known and a top band, and a
 * link so whoever reads it can play.
 */
export function shareText(score, week, standing = null) {
  const squares = score.rows.map((r) => (r.inBest ? "\u{1F7E9}" : "\u2B1C")).join("");
  const lines = [`Lineup CFB ${GAME_NAME} \u00B7 Week ${week}`, `${squares} ${score.total}/${score.bestTotal}`];
  const band = standingBand(standing);
  // The band, never the player count: with a small field the count says more
  // about how few are playing than about the score.
  if (band?.isTop) lines.push(`Finished in the ${band.label.toLowerCase()}`);
  lines.push(SHARE_URL);
  return lines.join("\n");
}

/** The captain, in shared text: ©️ is the usual fantasy shorthand and renders everywhere. */
export const CAPTAIN_MARK = "\u00A9\uFE0F";

/**
 * The invite, shared once picks are in: the week, each pick with its spread
 * (©️ on the captain), and the link. `rowsBySlug` maps a slug to a picker row
 * (buildPickRows or slateToRows) for its name and line.
 *
 *   My Pick Three for week 6:
 *   Georgia -21.5 ©️
 *   Oregon -1.5
 *   Texas +6.5
 *   Think you can beat me?
 *   https://www.lineupcfb.com/pick-three
 */
export function inviteText(week, picks, captain, rowsBySlug = {}) {
  const lines = (picks || []).map((slug) => {
    const row = rowsBySlug[slug];
    const name = row?.name || slug;
    const line = !row ? "" : row.bye ? "bye" : formatSpreadValue(row.spread) || "no line";
    return `${name}${line ? ` ${line}` : ""}${slug === captain ? ` ${CAPTAIN_MARK}` : ""}`;
  });
  return [`My Pick Three for week ${week}:`, ...lines, "Think you can beat me?", SHARE_URL].join("\n");
}

/** "10 of a possible 33". */
export const scoreLine = (score) => `${score.total} of a possible ${score.bestTotal}`;

// --- Where the player finished ---
//
// pick_three_standing (migration 083) returns counts only: how many players,
// how many other players scored more, how many tied. Nothing about any other
// player. Placing the caller from those counts happens here.

/**
 * A standing as the player reads it, or null while it is withheld (fewer than
 * ten players, or no entry). Ties count as half a place, so a tie for the top
 * is not read as winning outright.
 *
 * Screens show the band and `beatPct`, never `players`: while the field is
 * small the count would say more about how few are playing than about the
 * score.
 *
 * `beatPct` is the share of the other players this score beat. `label` is the
 * band it falls in: Top 10%, Top 25%, Top half or Lower half. Plain positions,
 * not "better than N%", which read awkwardly at the low end.
 */
export function standingBand(standing) {
  if (!standing?.ready || !(standing.players > 1)) return null;
  const { players, higher, tied } = standing;
  const others = players - 1;
  const beatPct = Math.round((100 * (others - higher - tied + tied / 2)) / others);
  const top = 100 - beatPct;
  const band = [10, 25, 50].find((limit) => top <= limit);
  const label = band === 50 ? "Top half" : band ? `Top ${band}%` : "Lower half";
  return {
    players,
    beatPct,
    isTop: Boolean(band),
    label,
  };
}

/** Standing shown before enough players have picked. */
export const STANDING_WAITING = "Your standing appears once 10 players have picked.";

// --- A draft made before signing up ---
//
// A signed-out visitor chooses three teams on /pick-three. They are held on
// this device, then saved to the account on the picker the first time the new
// player lands there. A draft belongs to one week: a stale one is dropped.
// Every read and write is wrapped, like the daily games' storage.

const DRAFT_KEY = "pick-three:draft";

/** `{ teams, captain }` for this week, or null. A captain not among the teams is dropped. */
export function loadDraft(week) {
  try {
    const saved = JSON.parse(localStorage.getItem(DRAFT_KEY));
    if (saved && saved.week === week && Array.isArray(saved.teams) && saved.teams.length <= PICKS) {
      return { teams: saved.teams, captain: saved.teams.includes(saved.captain) ? saved.captain : null };
    }
  } catch {
    /* nothing saved, or storage unavailable */
  }
  return null;
}

export function saveDraft(week, teams, captain = null) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ week, teams, captain: teams.includes(captain) ? captain : null }));
  } catch {
    /* the picks just are not remembered across the sign up */
  }
}

export function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* nothing to clear */
  }
}

// --- The signed-out slate ---

/**
 * pick_three_slate rows as picker rows, the same shape buildPickRows makes from
 * the teams map, which a signed-out visitor does not have. A team with no
 * opponent is on a bye.
 */
export function slateToRows(slate) {
  // Form arrived with migration 087. A slate without it gets no form line,
  // rather than every team reading as a bye.
  const hasForm = (slate?.teams || []).some((t) => "lastWeek" in t);
  return (slate?.teams || []).map((t) => ({
    slug: t.id,
    name: t.school,
    logo: teamLogoUrl(t.school),
    conference: t.conference || null,
    bye: !t.opponent,
    isHome: t.opponent ? t.isHome : null,
    opponent: t.opponent || null,
    kickoff: t.kickoff ? new Date(t.kickoff) : null,
    tbd: Boolean(t.tbd),
    // The server's lock time when it sent one, the same rule otherwise.
    lockAt: t.lockAt ? new Date(t.lockAt) : lockAtFor(t.kickoff, t.tbd),
    spread: typeof t.spread === "number" ? t.spread : null,
    formWeek: hasForm ? slate.week ?? null : null,
    lastWeek: t.lastWeek == null ? null : Number(t.lastWeek),
    seasonAvg: t.seasonAvg == null ? null : Number(t.seasonAvg),
  }));
}

/**
 * The slate's window in the shape weekWindow gives, so describeWindow reads
 * either. Open while the server says so and the last lock is still ahead.
 */
export function slateWindow(slate, now = new Date()) {
  if (!slate?.lastLockAt || !slate.startsAt) return null;
  const lastLockAt = new Date(slate.lastLockAt);
  const { nextLockAt } = lockSpan(slateToRows(slate).map((r) => r.lockAt), now);
  return {
    phase: slate.open && now < lastLockAt ? "open" : "live",
    kickoff: { date: new Date(slate.startsAt), tbd: Boolean(slate.startsTbd) },
    nextLockAt,
    lastLockAt,
  };
}

/**
 * Picks that can no longer be kept from a draft: teams that locked while the
 * visitor was away or signing up. Returns the picks still open and the names
 * of the ones dropped, so the screen can say what happened.
 */
export function dropLockedPicks(picks, rows, now = new Date()) {
  const bySlug = Object.fromEntries((rows || []).map((r) => [r.slug, r]));
  const kept = [];
  const dropped = [];
  (picks || []).forEach((slug) => {
    const row = bySlug[slug];
    if (row && isRowLocked(row, now)) dropped.push(row.name);
    else kept.push(slug);
  });
  return { kept, dropped };
}

/** "Ohio State kicked off", "Ohio State and Georgia kicked off". */
export function droppedNote(names) {
  if (!names.length) return null;
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${list} kicked off before your picks were saved, so ${names.length === 1 ? "it was" : "they were"} removed. Pick ${names.length === 1 ? "another team" : "other teams"}.`;
}

/**
 * The teams map with fresher weekly points laid over it, `{ slug: weeklyPoints }`
 * from team_season_stats. Teams the fresh read did not include keep what they
 * had. The same object back when there is nothing fresh, so memos hold.
 */
export function withLivePoints(teams, livePoints) {
  if (!livePoints) return teams;
  const merged = {};
  Object.entries(teams || {}).forEach(([slug, team]) => {
    const weeklyPoints = livePoints[slug];
    merged[slug] = weeklyPoints ? { ...team, currentSeason: { ...team.currentSeason, weeklyPoints } } : team;
  });
  return merged;
}

// --- How it scores, for the signed-out page ---
//
// The spread bands as the scorer applies them (calculateTeamFantasyPoints in
// backend/scoring.js, explainTeamFantasyPoints in utils/scoreBreakdown.js):
// points for beating the spread by this much, the same number off for missing
// it by this much. Landing exactly on the number scores nothing. Copy only:
// pickThree.test.js checks every band against explainTeamFantasyPoints, so
// this table cannot drift from the real scoring.
export const SPREAD_BANDS = [
  { label: "Up to 7", from: 0.5, to: 7, points: 1 },
  { label: "7.5 to 14", from: 7.5, to: 14, points: 2 },
  { label: "14.5 to 19.5", from: 14.5, to: 19.5, points: 3 },
  { label: "20 or more", from: 20, to: null, points: 5 },
];
export const WIN_POINTS = 5;
export const LOSS_POINTS = -3;
export const UNDERDOG_WIN_BONUS = 3;

// --- Your season ---

/**
 * One player's season, from their entries (`{ [week]: { teams, captain } }`),
 * the teams map, and their standing per week (`{ [week]: standing }`, each as
 * pick_three_standing returns it, or missing).
 *
 * `rows` runs newest first: the week, the points scored, the best possible,
 * and the placing band when there is one. `best` is the highest-scoring week
 * (the earlier one on a tie), `average` the mean points a week (one
 * decimal), and `bestBand` the best placing. Weeks after
 * `throughWeek` are left out.
 */
export function seasonSummary(entries, teams, standings = {}, throughWeek = Infinity) {
  const rows = Object.entries(entries || {})
    .map(([w, entry]) => [Number(w), entry])
    .filter(([week, entry]) => week <= throughWeek && entry?.teams?.length === PICKS)
    .sort((a, b) => b[0] - a[0])
    .map(([week, entry]) => {
      const score = scoreEntry(teams, entry.teams, week, entry.captain);
      return { week, total: score.total, bestTotal: score.bestTotal, band: standingBand(standings[week]) };
    });

  const best = rows.reduce((top, r) => (!top || r.total > top.total || (r.total === top.total && r.week < top.week) ? r : top), null);
  const bestBand = rows.reduce((top, r) => (r.band && (!top || r.band.beatPct > top.beatPct) ? r.band : top), null);
  const average = rows.length ? Math.round((rows.reduce((sum, r) => sum + r.total, 0) / rows.length) * 10) / 10 : null;
  return { rows, played: rows.length, best, bestBand, average };
}
